/**
 * OverbookingDashboard
 * ──────────────────────────────────────────────────────────────────────
 * Overbooking Efficiency Dashboard for the Analytics tab
 * (echo_overbooking_engine_spec.md §Analytics).
 *
 * Pure UI; caller owns data + handlers.
 *
 * Props:
 *   data:      { windowLabel, metrics: {k:{value,unit,label,sub}}, weeklyCard: {...} }
 *   onApplyRecommendation(providerId, newLimit)
 *   onDismissRecommendation()
 */
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../lib/utils';
import { Sparkles, AlertTriangle, TrendingUp, TrendingDown, CheckCircle2 } from 'lucide-react';

const MetricCard = ({ id, m, hint }) => (
  <Card className="border-slate-200" data-testid={`ob-metric-${id}`}>
    <CardContent className="p-4">
      <div className="text-[10px] uppercase text-slate-400 font-semibold">{m.label}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="text-2xl font-semibold text-slate-900">{m.value}</span>
        {m.unit && <span className="text-sm text-slate-500">{m.unit}</span>}
      </div>
      <p className="text-[10px] text-slate-500 mt-0.5">{m.sub}</p>
      {hint && <p className={cn('text-[10px] font-medium mt-1', hint.cls)}>{hint.text}</p>}
    </CardContent>
  </Card>
);

const OverbookingDashboard = ({ data, onApplyRecommendation, onDismissRecommendation }) => {
  if (!data) return null;
  const m = data.metrics;
  const wk = data.weeklyCard;

  return (
    <div className="space-y-4" data-testid="ob-dashboard">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-violet-500" />
          <h2 className="text-lg font-semibold text-slate-900">Overbooking efficiency</h2>
        </div>
        <Badge className="text-[10px] border-0 bg-slate-100 text-slate-600">
          {data.windowLabel}
        </Badge>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <MetricCard id="fillRate" m={m.fillRate} />
        <MetricCard
          id="collisionRate"
          m={m.collisionRate}
          hint={{
            cls: 'text-emerald-600',
            text: 'Below industry avg — healthy',
          }}
        />
        <MetricCard id="revenueRecovered" m={m.revenueRecovered} />
        <MetricCard id="avgLeadTime" m={m.avgLeadTime} />
        <MetricCard id="waitlistConversion" m={m.waitlistConversion} />
        <MetricCard id="aiAccuracy" m={m.aiAccuracy} />
      </div>

      {/* Weekly intelligence card */}
      <Card className="border-violet-200" data-testid="ob-weekly-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" />
            <h3 className="text-sm font-semibold text-slate-900">
              Echo overbooking — last 7 days
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[11px]">
            <div>
              <span className="text-slate-400">Slots flagged at-risk</span>
              <p className="text-slate-800 font-semibold text-sm">{wk.slotsFlagged}</p>
            </div>
            <div>
              <span className="text-slate-400">Contingencies placed</span>
              <p className="text-slate-800 font-semibold text-sm">{wk.contingenciesPlaced}</p>
            </div>
            <div>
              <span className="text-slate-400">Conversions</span>
              <p className="text-slate-800 font-semibold text-sm">
                {wk.conversions} →{' '}
                <span className="text-emerald-600">
                  {Math.round((wk.conversions / wk.contingenciesPlaced) * 100)}% fill rate
                </span>
              </p>
            </div>
            <div>
              <span className="text-slate-400">Collisions</span>
              <p className="text-slate-800 font-semibold text-sm">
                {wk.collisions} →{' '}
                <span className={wk.collisions > 2 ? 'text-amber-600' : 'text-emerald-600'}>
                  {Math.round((wk.collisions / Math.max(1, wk.contingenciesPlaced)) * 100 * 10) / 10}%
                </span>
              </p>
            </div>
            <div>
              <span className="text-slate-400">Revenue recovered</span>
              <p className="text-emerald-600 font-semibold text-sm">{wk.revenueRecovered}</p>
            </div>
            <div>
              <span className="text-slate-400">AI accuracy</span>
              <p className="text-slate-800 font-semibold text-sm">{wk.accuracy}%</p>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">
              Provider breakdown
            </div>
            <div className="space-y-1">
              {wk.providerBreakdown.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2 text-[11px]"
                  data-testid={`ob-provider-row-${p.id}`}
                >
                  <span className="font-medium text-slate-800 w-32 truncate">{p.name}</span>
                  <span className="text-slate-600">{p.flags} flags</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-600">{p.filled} filled</span>
                  <span className="text-slate-400">·</span>
                  <span className={p.collisions > 0 ? 'text-amber-700 font-medium' : 'text-slate-600'}>
                    {p.collisions} collision{p.collisions === 1 ? '' : 's'}
                  </span>
                  {p.warn && (
                    <AlertTriangle className="h-3 w-3 text-amber-500 ml-1" title="Review limit" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {wk.recommendation && (
            <div className="rounded-md bg-violet-50 border border-violet-200 p-3 space-y-2" data-testid="ob-weekly-recommendation">
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-violet-500" />
                <span className="text-[11px] font-semibold text-violet-800">
                  Recommendation
                </span>
              </div>
              <p className="text-[11px] text-slate-700">{wk.recommendation}</p>
              <div className="flex gap-2 pt-0.5">
                <Button
                  size="sm"
                  className="h-7 text-[10px] bg-[#6b5ce7] hover:bg-[#5a4bd6]"
                  onClick={() => onApplyRecommendation?.(wk.recommendationProviderId, wk.recommendationNewLimit)}
                  data-testid="ob-weekly-apply"
                >
                  Apply
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[10px]"
                  onClick={onDismissRecommendation}
                  data-testid="ob-weekly-dismiss"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OverbookingDashboard;
